import { useState } from "react";

import { getDeleteProfileErrorMessage } from "app/pages/sales-intelligence/modules/employee-profiles/utils/getDeleteProfileErrorMessage";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";
import { usePlaybookToasts } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybookToasts";
import RemoveProfileModal from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/components/RemoveProfileModal/RemoveProfileModal";
import { useCreateContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useCreateContactProfileMutation";
import { useDeleteContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useDeleteContactProfileMutation";
import { useUpdateContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useUpdateContactProfileMutation";
import {
    ProfileAccessLevel,
    type ProfileDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import ProfileModal from "app/pages/sales-intelligence/sub-modules/profile-modal/ProfileModal";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

type PlaybookProfileModalContainerProps = {
    isSharedWithAccount: boolean;
    profileForEdit: ProfileDto | null;
    isModalOpened: boolean;
    onClose: () => void;
    onProfileCreated: (profileId: string) => void;
    onProfileDeleted: (profileId: string) => void;
};

export const PlaybookProfileModalContainer = ({
    isSharedWithAccount,
    profileForEdit,
    isModalOpened,
    onClose,
    onProfileCreated,
    onProfileDeleted,
}: PlaybookProfileModalContainerProps) => {
    const [isDeleteModalOpened, setDeleteModalOpened] = useState(false);
    const playbooksTracking = usePlaybooksTrackingService();
    const { showError, showErrorMessage } = usePlaybookToasts();

    const { mutateAsync: createProfile, isPending: isCreating } = useCreateContactProfileMutation();
    const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateContactProfileMutation();
    const { mutateAsync: deleteProfile, isPending: isDeleting } = useDeleteContactProfileMutation();

    const isBeingSubmitted = isCreating || isUpdating;

    const handleSubmit = async (payload: Omit<ProfileDto, "id" | "isShared">) => {
        if (profileForEdit === null) {
            playbooksTracking.trackProfileSubmit("create");
            const createdProfile = await createProfile({
                ...payload,
                isShared: isSharedWithAccount,
            });
            onProfileCreated(createdProfile.id);
            onClose();
            return;
        }

        playbooksTracking.trackProfileSubmit("edit");
        await updateProfile({
            ...payload,
            id: profileForEdit.id,
            isShared: profileForEdit.accessLevel === ProfileAccessLevel.Account,
        });
        onClose();
    };

    const handleDelete = async (id: ProfileDto["id"]) => {
        playbooksTracking.trackDeleteProfileConfirm();

        try {
            await deleteProfile(id);
            onProfileDeleted(id);
            setDeleteModalOpened(false);
            onClose();
        } catch (error) {
            const serverMessage = await getDeleteProfileErrorMessage(error);

            // A rejected delete is an expected outcome rather than a fault, so it is not reported to Sentry.
            if (serverMessage) {
                showErrorMessage(serverMessage);
                return;
            }

            sentryErrorTrackerWithScope({
                errorMessage: "Failed to delete employee profile from the playbook wizard modal",
                e: error as Error,
                tags: { component: "playbooks" },
                params: { extra: { profileId: id } },
            });
            showError();
        }
    };

    const handleDeleteClick = () => {
        playbooksTracking.trackProfileDeleteFromModalOpen();
        setDeleteModalOpened(true);
    };

    const handleDeleteCancel = () => {
        playbooksTracking.trackDeleteProfileCancel();
        setDeleteModalOpened(false);
    };

    return (
        <>
            <ProfileModal
                profile={profileForEdit}
                isBeingDeleted={isDeleting}
                isBeingSubmitted={isBeingSubmitted}
                isOpened={isModalOpened ? !isDeleteModalOpened : false}
                onCancel={onClose}
                onSubmit={handleSubmit}
                onDeleteClick={handleDeleteClick}
            />
            {profileForEdit ? (
                <RemoveProfileModal
                    profile={profileForEdit}
                    isLoading={isDeleting}
                    isOpened={isDeleteModalOpened}
                    onSubmit={handleDelete}
                    onCancel={handleDeleteCancel}
                />
            ) : null}
        </>
    );
};
